"use client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CacheKeys } from "../../constants/CacheKeys";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import { useGetLinkFields } from "../../hooks/useTools";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Tool, ToolTypes } from "../../models/Tool";
import { ReportType } from "../../services/ReportService";
import { mergeToolsFieldsWithAssociatedActivitiesFields } from "../../services/ToolService";
import { getToolColumnsFromColumnOrder } from "../../services/ViewService";
import { MassCreateDraftButton } from "../list/MassCreateDraftButton";
import { PaginatedList } from "../list/PaginatedList";

const PublishedLinkList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onMassCreateDraftSuccess = (createdVersionIds: string[]) => {
    window.sessionStorage.setItem(
      SelectionPersistenceKeys.LinksDrafts,
      JSON.stringify(createdVersionIds)
    );
    navigate(`${ClientRoutes.Links}?view=drafts`);
  };

  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetLinkFields();

  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const columns = useMemo<VsaTableColumnDefinition<Tool>[]>(() => {
    if (
      viewDefinitionLoading ||
      !viewDefinition ||
      !activityViewDefinition ||
      activityViewDefinitionLoading
    ) {
      return [];
    }

    return getToolColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      activityViewDefinition.fields,
      viewDefinition.publishedFieldOrder,
      ToolTypes.Link,
      false
    );
  }, [
    viewDefinition,
    viewDefinitionLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
  ]);

  return (
    <PaginatedList<Tool>
      apiEndpoint={ApiEndpoints.ToolLinksCurrent}
      columns={columns}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      searchPlaceholder={t("searchTool")}
      postQueryTransformation={mergeToolsFieldsWithAssociatedActivitiesFields}
      cacheKey={CacheKeys.Link.LinkList}
      filtersEndpoint={ApiEndpoints.ToolLinkFilters}
      reportType={ReportType.PublishedLinks}
      storageKey="links:published"
      libraryFilterScope={{ listType: "link" }}
      selectedActions={(selectedVersionIds) => (
        <MassCreateDraftButton
          versionIds={selectedVersionIds}
          entityType="tool"
          onSuccess={onMassCreateDraftSuccess}
        />
      )}
    />
  );
};

export default PublishedLinkList;
